import { ACTION_STATES, Direction, EffectLimit, Facing, HARVEST_STRIP, MotionDetector, ORIGIN_RISE, Predictor, headingOf, stepsWhile, trueCoordinate, type Band, type CollisionGrid, type MovementState, type Offsets } from "odyssey-shared";
import type { CharacterFigure } from "./character.ts";
import { INPUT_INTERVAL_MS, type GameConnection } from "./connection.ts";
import { ChainSchedule } from "./fight/chain-schedule.ts";
import type { FightBody } from "./fight/fight.ts";
import { gameReport } from "./report.ts";
import type { RunAsk } from "./run/page.ts";
import { steadyAxis } from "./steady-axis.ts";

const SOFT_DECAY_MS = 100;
const FIRM_DECAY_MS = 50;
const MAX_INPUT_CATCH_UP = 2;

/**
 * The local player's character (CLIENT.md sections 2 to 4): the predictor that runs its movement ahead of the
 * server, the inputs sent once per tick interval, the render-side smoothing of corrections, and its sprite, drawn
 * between the predicted states before and after each frame's ticks. The input clock, the input seq and the correction's
 * decay run on across sessions (the seq so that a map server a same-socket handoff joins never holds a seq above the
 * page's, P1.4-T200); a session gives it a predictor and a sprite and takes them away.
 *
 * It is the fight's body (P2.1-T4, `fight/fight.ts`): a swing starts its strip and the predicted turn on intent and a
 * refusal cancels the strip back to idle; a hit taken plays the hurt; `vitals` saying dead lay it down on the death
 * strip, held, and stop its movement, the page still sending an input a tick with no direction so the server's
 * acknowledgements, and with them the respawn's position, keep arriving; alive again stands it up. The movement limits
 * the server confirmed through the owner's `effects` frame go to its predictor, which steps and replays every input
 * under them (ADR-079 decision 4), so a stunned character's page predicts no movement once the stun arrives. A chain's
 * later strips start on their schedule from the press as the frames are drawn, and its root rides the predicted turn
 * (ADR-073's amendment "the Strike combo and the Heavy Strike chain", item 8). The character is drawn at its true point
 * (ADR-116's amendment of 2026-10-07): the predicted lattice point and the offsets the predictor carries, as every
 * page draws every character from the offsets its views carry, so a walk whose lattice steps are 2 and 3 sixteenths
 * moves at one pace, turning, sliding and stopping included. It is drawn running while it moves and its last input was
 * predicted to run (ADR-116 decisions 12 and 14): the prediction reads the Stamina, the limits and the hold the map
 * server last told the page, so the strip follows the server's word a round trip sooner than its own snapshot's
 * running bit would bring it, and is wrong only for the step or two the prediction itself is. A hop (`hop`, ADR-116
 * decisions 17 to 19) plays its `jump` strip on the press, as a swing starts its own, and the next input sent carries
 * the jump bit; the map server's state 8 tells every other page.
 */
export class LocalPlayer implements FightBody {
  private predictor: Predictor | null = null;
  /**
   * The last input seq the page sent with an ended predictor; the next predictor goes on from it, so a map server that
   * a handoff joins on the same socket never holds a seq above the page's from inputs still in flight (P1.4-T200).
   */
  private seqSent = 0;
  private character: CharacterFigure | null = null;
  private readonly motion = new MotionDetector();
  private walks = false;
  private renderedAt = { x: 0, y: 0 };
  private previousPredicted = { x: 0, y: 0 };
  private drawnY = Number.NaN;
  private offset = { x: 0, y: 0, decayMs: 0 };
  private inputAccumulator = 0;
  private deadNow = false;
  private sent: Direction = Direction.None;
  /**
   * Whether the last input sent was predicted to run (`Predictor.input`'s `ran`): what the character's run strip is
   * drawn from between the map server's words (ADR-116 decision 14). An input with no direction never ran.
   */
  private ran = false;
  private runs = false;
  /**
   * Whether the next input sent carries the jump bit: set by a hop played, cleared by the input that carries it.
   */
  private jumpAsked = false;
  /**
   * The scene time of the last hop played, in ms; minus infinity before any.
   */
  private hoppedAt = Number.NEGATIVE_INFINITY;
  private readonly pose: { x: number; y: number; facing: Facing; moving: boolean; running: boolean } = { x: 0, y: 0, facing: 0, moving: false, running: false };
  private worldScale = 0;
  private limits = 0;
  private moveSpeed = 0;
  private readonly chain = new ChainSchedule();
  private chainAbility = 0;
  private weaponDrawn = false;

  /**
   * Told when a chain's later blow starts its strip, with the chain's ability number, so the page sounds each blow as
   * it sounds the first; null for none.
   */
  public onChainBlow: ((ability: number) => void) | null = null;

  /**
   * Where the sprite's feet are drawn this frame, in whole world pixels, which world-attached UI reads.
   */
  public readonly drawn = { x: 0, y: 0 };

  /**
   * The predictor, while a session has one.
   *
   * @returns The predictor, or null outside a session.
   */
  public get prediction(): Predictor | null {
    return this.predictor;
  }

  /**
   * The sprite, while a session has one.
   *
   * @returns The sprite, or null outside a session.
   */
  public get sprite(): CharacterFigure | null {
    return this.character;
  }

  /**
   * How far the character's art reaches above its feet this frame, which its plate stands on: the head height raised
   * by a hop's lift (ADR-116's third amendment), so the plate rises with the head.
   *
   * @param head How far the standing art reaches above the feet, in world pixels.
   * @returns The head height plus the sprite's lift; the head height alone outside a session.
   */
  public plateHead(head: number): number {
    return head + (this.character?.lift ?? 0);
  }

  /**
   * Where the character is drawn this frame in sixteenths, before rounding to pixels.
   *
   * @returns The rendered position.
   */
  public get rendered(): { readonly x: number; readonly y: number } {
    return this.renderedAt;
  }

  /**
   * Starts a session's character at its spawn: a predictor over the grid and a sprite drawn there.
   *
   * @param start The spawn state.
   * @param grid The collision grid the predictor moves against.
   * @param sprite The sprite to draw.
   * @param walkSpeed The rules' `walk_speed`, in speed units, which the predictor steps at.
   * @param runSpeed The rules' `run_speed`, in speed units, the length of a step the predictor ran.
   */
  public begin(start: MovementState, grid: CollisionGrid, sprite: CharacterFigure, walkSpeed: number, runSpeed: number): void {
    this.predictor = new Predictor(start, grid, walkSpeed, runSpeed, this.seqSent + 1);
    this.predictor.setLimits(this.limits);
    this.predictor.setMoveSpeed(this.moveSpeed);
    this.renderedAt = { x: start.x, y: start.y };
    this.previousPredicted = { x: start.x, y: start.y };
    this.drawnY = Number.NaN;
    this.character = sprite;
    sprite.drawn = this.weaponDrawn;
  }

  /**
   * Tells the character whether its weapon is drawn (the stance, `fight/stance.ts`), which a sheet with drawn strips
   * shows in its idle and walk. It is kept across sessions, so a sprite begun later wears the stance of the moment.
   *
   * @param drawn Whether the weapon is drawn.
   */
  public wear(drawn: boolean): void {
    this.weaponDrawn = drawn;
    if (this.character !== null) {
      this.character.drawn = drawn;
    }
  }

  /**
   * Carries the character across a seam as the new map's `welcome` moves the render origin (ADR-147 decisions 6 and 18):
   * the prediction moves into the new map's frame with its pending inputs and seq (`Predictor.rebase`), and the rendered
   * and drawn points move by the same whole number of pixels as every other sprite and the camera, so nothing on screen
   * moves. The sprite, the motion and the input clock carry on.
   *
   * @param grid The new map's grid.
   * @param dx What to add across, in sixteenths.
   * @param dy The same down.
   * @param scale World pixels a sixteenth.
   */
  public cross(grid: CollisionGrid, dx: number, dy: number, scale: number): void {
    const predictor = this.predictor;
    if (predictor === null) {
      return;
    }
    predictor.rebase(grid, dx, dy);
    this.renderedAt = { x: this.renderedAt.x + dx, y: this.renderedAt.y + dy };
    this.previousPredicted = { x: this.previousPredicted.x + dx, y: this.previousPredicted.y + dy };
    if (!Number.isNaN(this.drawnY)) {
      this.drawnY += Math.round(dy * scale);
    }
    this.drawn.x += Math.round(dx * scale);
    this.drawn.y += Math.round(dy * scale);
    this.motion.shift(dx, dy);
  }

  /**
   * Ends the session's character: the sprite is destroyed and the predictor dropped, its last seq kept for the next.
   */
  public end(): void {
    this.seqSent = this.predictor?.lastSeq ?? this.seqSent;
    this.chain.clear();
    this.character?.destroy();
    this.character = null;
    this.predictor = null;
    this.deadNow = false;
    this.walks = false;
    this.ran = false;
    this.runs = false;
  }

  /**
   * Whether the character is dead, as its `vitals` last said.
   *
   * @returns True while dead.
   */
  public get dead(): boolean {
    return this.deadNow;
  }

  /**
   * The seq of the last input sent, which an act sent now follows.
   *
   * @returns The seq; outside a session 0, and in a session before its first input the last seq the page sent before it (0 on a fresh page).
   */
  public get inputSeq(): number {
    return this.predictor?.lastSeq ?? 0;
  }

  /**
   * Hands the movement limits the server confirmed to the predictor, and to the next one a session makes.
   *
   * @param limits The `EffectLimit` bits in force, 0 for none.
   */
  public limit(limits: number): void {
    this.limits = limits;
    this.predictor?.setLimits(limits);
  }

  /**
   * Hands the move speed the server confirmed to the predictor, and to the next one a session makes.
   *
   * @param percent The sum of the effects' `moveSpeed`, 0 for none.
   */
  public slow(percent: number): void {
    this.moveSpeed = percent;
    this.predictor?.setMoveSpeed(percent);
    gameReport.moveSpeed = percent;
  }

  /**
   * The limits the server last confirmed for the character (`limit`), which the interaction reads so that E and its
   * prompt stand down while acting is forbidden, as the map server refuses an interact then (ADR-079's amendment of
   * 2026-10-07, point 5).
   *
   * @returns The `EffectLimit` bits in force, 0 for none and outside a session.
   */
  public get effectLimits(): number {
    return this.limits;
  }

  /**
   * Starts a swing on intent: the ability's strip plays once and the predictor turns the character at this point of
   * its inputs (ADR-073 decision 15).
   *
   * @param ability The ability's number.
   * @param strip The ability's strip.
   * @param facing The facing the aim gives.
   * @param nowMs Scene time in ms.
   * @param rootInputs The inputs after the act its chain roots, 0 for an act with no chain.
   * @returns The seq of the last input sent, the act's `afterSeq`; 0 outside a session.
   */
  public swing(ability: number, strip: string, facing: Facing, nowMs: number, rootInputs: number): number {
    const predictor = this.predictor;
    if (predictor === null) {
      return 0;
    }
    this.chain.clear();
    this.chainAbility = ability;
    this.character?.playback.play(strip, nowMs);
    return predictor.turn(facing, ability, rootInputs);
  }

  /**
   * Schedules a chain's later strips from the press, each started as the frame that reaches its time is drawn and told
   * to `onChainBlow` with the ability the swing that began it named.
   *
   * @param strips The later blows' strips, in order.
   * @param offsetsMs Each one's time after the press, in ms.
   * @param nowMs The press's scene time in ms.
   */
  public follow(strips: readonly string[], offsetsMs: readonly number[], nowMs: number): void {
    this.chain.start(strips, offsetsMs, nowMs);
  }

  /**
   * Swaps the strip playing for the one the server chose, from the same start; nothing when another strip plays.
   *
   * @param from The strip the page started.
   * @param to The strip the server chose.
   */
  public swapStrip(from: string, to: string): void {
    this.character?.playback.swap(from, to);
  }

  /**
   * Ends the chain: its later strips not yet started are dropped and its predicted root ends at the last input made.
   */
  public interrupt(): void {
    this.chain.clear();
    this.predictor?.endRoot();
  }

  /**
   * Cancels a refused ability's swing: the predictor drops its newest turn and hold, since the map server turns and
   * holds nothing for a refused act, and its strip goes back to idle if it is the one playing; a later swing of another
   * ability, a hurt or the death held is left as it is.
   *
   * @param ability The refused ability's number.
   * @param strip The refused ability's strip.
   */
  public cancelSwing(ability: number, strip: string): void {
    this.predictor?.release(ability);
    this.chain.clear();
    const playback = this.character?.playback;
    if (playback !== undefined && playback.strip === strip && ACTION_STATES.has(strip)) {
      playback.clear();
    }
  }

  /**
   * Plays a hop on a press of the jump key (ADR-116 decisions 17 to 19), the own character's strip started on the
   * press as a swing's is, and asks the next input to carry the jump bit. The page refuses what it knows the map server
   * refuses, so its own hop and everyone else's agree: none while dead, while its effects stop it moving, while a strip
   * of an act it started plays (a swing, whose facing the map server holds, a chain's blows, which root it, and a
   * dash's lunge), or sooner than `waitMs` after its last hop; the hold of a freeze is the caller's. A sheet without a `jump` strip draws the character by its motion, as
   * for any strip it lacks.
   *
   * @param nowMs Scene time in ms.
   * @param waitMs The least time between two hops: the rules' `jump_ticks` in ms.
   * @returns True when the hop played and the bit is asked for.
   */
  public hop(nowMs: number, waitMs: number): boolean {
    const strip = this.character?.playback.strip ?? null;
    if (this.predictor === null || this.deadNow || (this.limits & EffectLimit.Move) !== 0 || (strip !== null && ACTION_STATES.has(strip)) || nowMs - this.hoppedAt < waitMs) {
      return false;
    }
    this.hoppedAt = nowMs;
    this.jumpAsked = true;
    this.character?.playback.play("jump", nowMs);
    return true;
  }

  /**
   * Starts or ends the harvest loop (ADR-142's amendment of 2026-10-31) as the page's hold starts or ends: the own
   * character shows the sickle's slash over and over from the hold's `hold` to its end, as every watcher sees it from
   * the harvest state. A loop already running is left, none starts while dead or while a swing, a chain's blow or a
   * dash plays, and ending it clears the loop alone: a hurt that cut the hold, or the death held, stays.
   *
   * @param active Whether a hold runs.
   * @param nowMs Scene time in ms.
   */
  public harvesting(active: boolean, nowMs: number): void {
    const playback = this.character?.playback;
    if (playback === undefined) {
      return;
    }
    if (!active) {
      if (playback.strip === HARVEST_STRIP) {
        playback.clear();
      }
      return;
    }
    const playing = playback.strip;
    if (this.deadNow || playing === HARVEST_STRIP || (playing !== null && ACTION_STATES.has(playing))) {
      return;
    }
    playback.loop(HARVEST_STRIP, nowMs);
  }

  /**
   * The frame of the harvest strip the own character shows, the blow landing at `HARVEST_GRAB_FRAME`.
   *
   * @returns The frame, or -1 while the strip does not show.
   */
  public get harvestFrame(): number {
    const playback = this.character?.playback;
    return playback !== undefined && playback.strip === HARVEST_STRIP ? playback.frame : -1;
  }

  /**
   * Plays the hurt strip for a hit taken, unless a swing is playing, which it lets finish.
   *
   * @param nowMs Scene time in ms.
   */
  public hurt(nowMs: number): void {
    this.character?.playback.hurt(nowMs);
  }

  /**
   * Lays the character down on the death strip, held, or stands it up.
   *
   * @param dead Whether it is dead.
   * @param nowMs Scene time in ms.
   */
  public setDead(dead: boolean, nowMs: number): void {
    this.deadNow = dead;
    const playback = this.character?.playback;
    this.chain.clear();
    if (dead) {
      playback?.hold("death", nowMs, false);
    } else {
      playback?.clear();
    }
  }

  /**
   * Where a swing starts, in world pixels: the centre of the predicted feet box (ADR-073 decision 7), where the map
   * server judges the swing.
   *
   * @param out Where the point is written.
   * @returns False outside a session or before the first frame drawn.
   */
  public origin(out: { x: number; y: number }): boolean {
    const predictor = this.predictor;
    if (predictor === null || this.worldScale === 0) {
      return false;
    }
    out.x = predictor.predicted.x * this.worldScale;
    out.y = (predictor.predicted.y - ORIGIN_RISE) * this.worldScale;
    return true;
  }

  /**
   * The predicted facing.
   *
   * @returns The facing, down outside a session.
   */
  public facing(): Facing {
    return this.predictor?.predicted.facing ?? Facing.Down;
  }

  /**
   * The way the character looks, for its cone of sight (ADR-061's amendment for P1.9-T75): the direction its last
   * input sent while it walks, else its predicted facing.
   *
   * @returns The heading in radians (`headingOf`).
   */
  public heading(): number {
    return headingOf(this.sent, this.facing());
  }

  /**
   * Reconciles the predictor with the server's state for the local player and starts a correction for the band the
   * error falls in.
   *
   * @param ackSeq The last input sequence the server applied.
   * @param server The server's position and facing for the player.
   * @param offsets The offsets of the player's true point in that state, as the snapshot carried them.
   */
  public reconcile(ackSeq: number, server: MovementState, offsets: Readonly<Offsets>): void {
    const result = this.predictor?.reconcile(ackSeq, server, offsets) ?? null;
    if (result !== null && result.band !== 0) {
      gameReport.largestCorrection = Math.max(gameReport.largestCorrection, result.error);
      this.correct(result.band);
    }
  }

  /**
   * Starts a render-side correction: the difference between where the marker is drawn and where the prediction now
   * says it is decays over the band's time; a snap is instant and forgets the motion, so a jump (a respawn at a
   * shrine) is not drawn as a step. The between-tick interpolation restarts from the corrected state.
   *
   * @param band The reconciliation band.
   */
  private correct(band: Band): void {
    const predictor = this.predictor;
    if (predictor === null) {
      return;
    }
    gameReport.corrections[band] += 1;
    const x = trueCoordinate(predictor.predicted.x, predictor.offsets.x);
    const y = trueCoordinate(predictor.predicted.y, predictor.offsets.y);
    this.previousPredicted = { x, y };
    if (band === 3) {
      this.offset = { x: 0, y: 0, decayMs: 0 };
      this.motion.reset();
      console.warn(`hard snap to ${predictor.predicted.x},${predictor.predicted.y}`);
      return;
    }
    this.offset = { x: this.renderedAt.x - x, y: this.renderedAt.y - y, decayMs: band === 1 ? SOFT_DECAY_MS : FIRM_DECAY_MS };
  }

  /**
   * Sends one input per tick interval (at most two on a long frame, the rest skipped and counted in the report). While
   * the character is dead each input carries no direction (ADR-073 decision 15). An input with a direction carries the
   * sprint bit while the player asks to run (ADR-116 decision 13), and the predictor steps it at the run's length when
   * the page expects the map server to let it run (decision 12), from the state the server last told it; the two can
   * differ for a step or two near empty Stamina, which the next acknowledgement corrects. The first input after a hop
   * carries the jump bit (ADR-116 decision 18), whether or not it has a direction.
   *
   * @param predictor The session's predictor.
   * @param delta Frame delta in ms.
   * @param intent Reads the movement keys' direction, once for each input sent.
   * @param connection The connection the inputs go out on.
   * @param run The page's run, asked once for each input sent; null for a page that never runs.
   */
  public sendInputs(predictor: Predictor, delta: number, intent: () => Direction, connection: GameConnection, run: RunAsk | null = null): void {
    this.inputAccumulator += delta;
    let inputs = 0;
    while (this.inputAccumulator >= INPUT_INTERVAL_MS && inputs < MAX_INPUT_CATCH_UP) {
      this.inputAccumulator -= INPUT_INTERVAL_MS;
      if (inputs === 0) {
        this.previousPredicted = { x: trueCoordinate(predictor.predicted.x, predictor.offsets.x), y: trueCoordinate(predictor.predicted.y, predictor.offsets.y) };
      }
      inputs += 1;
      const dir = this.deadNow ? Direction.None : intent();
      const sprint = run !== null && dir !== Direction.None && run.asks();
      const jump = this.jumpAsked;
      this.jumpAsked = false;
      const pending = predictor.input(dir, sprint && (run as RunAsk).allowed(this.limits, this.deadNow), jump);
      connection.sendInput(pending.seq, pending.dir, sprint, jump);
      this.sent = pending.dir;
      this.ran = pending.ran;
    }
    if (this.inputAccumulator >= INPUT_INTERVAL_MS) {
      gameReport.inputsSkipped += Math.floor(this.inputAccumulator / INPUT_INTERVAL_MS);
      this.inputAccumulator = 0;
    }
  }

  /**
   * Whether the character walked in the frame last drawn: its rendered position moved, it is alive and it plays
   * neither a dash nor its death, which is when its footsteps sound (ADR-085's amendment for P1.9-T84).
   *
   * @returns True while it walks.
   */
  public get walking(): boolean {
    return this.walks;
  }

  /**
   * Whether the character ran in the frame last drawn: it moved and its last input was predicted to run, which is
   * when its footsteps take the run's stride (ADR-116 decision 27).
   *
   * @returns True while it is drawn running.
   */
  public get running(): boolean {
    return this.runs;
  }

  /**
   * Draws the character from the true point of the state before this frame's ticks to that of the state after them by
   * the time into the current tick (so a 20 Hz simulation moves every frame, and a two-tick catch-up frame still draws one continuous span),
   * starts a chain's strip whose time has come and tells `onChainBlow`, smooths corrections, and rounds the sprite to
   * whole pixels, which `drawn` then holds. While both axes move alike, a diagonal walk, the two true coordinates lie
   * at different places within a pixel, and rounding each alone would step them a pixel on different frames; x is
   * rounded and y is an even number of whole pixels from it, the nearest to how far the two stand apart, so the world
   * moves alike on both axes every frame at a zoom of two and a half device pixels to a world pixel too (R-0078).
   * The drawn y is then kept steady (`steadyAxis`, ADR-032's note of 2026-10-10): it never steps a pixel back against
   * the rendered motion, as the end of a diagonal walk would draw it, and holds while the rendered y stands.
   * The character walks while its rendered position changed within the motion hold (ADR-036 item 3).
   *
   * @param predictor The session's predictor.
   * @param delta Frame delta in ms.
   * @param time Scene time in ms.
   * @param scale World pixels per sixteenth.
   */
  public draw(predictor: Predictor, delta: number, time: number, scale: number): void {
    this.worldScale = scale;
    if (this.offset.decayMs > 0) {
      const keep = Math.exp(-delta / this.offset.decayMs);
      this.offset = { x: this.offset.x * keep, y: this.offset.y * keep, decayMs: Math.abs(this.offset.x) + Math.abs(this.offset.y) < 0.5 ? 0 : this.offset.decayMs };
    }
    const predicted = predictor.predicted;
    const x = trueCoordinate(predicted.x, predictor.offsets.x);
    const y = trueCoordinate(predicted.y, predictor.offsets.y);
    const between = Math.min(1, this.inputAccumulator / INPUT_INTERVAL_MS);
    const previous = this.previousPredicted;
    const renderedYBefore = this.renderedAt.y;
    this.renderedAt = { x: previous.x + (x - previous.x) * between + this.offset.x, y: previous.y + (y - previous.y) * between + this.offset.y };
    const across = x - previous.x;
    const down = y - previous.y;
    const px = Math.round(this.renderedAt.x * scale);
    let py = Math.round(this.renderedAt.y * scale);
    if (across !== 0 && across === down) {
      py = px + 2 * Math.round(((y - x + this.offset.y - this.offset.x) * scale) / 2);
    } else if (across !== 0 && across === -down) {
      py = 2 * Math.round(((y + x + this.offset.y + this.offset.x) * scale) / 2) - px;
    }
    py = steadyAxis(py, this.drawnY, (this.renderedAt.y - renderedYBefore) * scale, this.renderedAt.y * scale);
    this.drawnY = py;
    const next = this.chain.due(time);
    if (next !== null) {
      this.character?.playback.play(next, time);
      this.onChainBlow?.(this.chainAbility);
    }
    const moving = this.motion.update(this.renderedAt.x, this.renderedAt.y, time);
    if (moving && this.character?.playback.strip === HARVEST_STRIP) {
      this.character.playback.clear();
    }
    const pose = this.pose;
    pose.x = px;
    pose.y = py;
    pose.facing = predicted.facing;
    pose.moving = moving;
    pose.running = this.ran;
    this.character?.setPose(pose, time);
    this.walks = moving && !this.deadNow && stepsWhile(this.character?.playback.strip ?? null);
    this.runs = moving && this.ran;
    this.drawn.x = px;
    this.drawn.y = py;
  }
}
